import {
    defineEntityConstellationDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { EntityConstellationDerivation } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toNaturalRenderedDigits } from '@/adapters/primitive/_pure/derivation/itemFactories';
import {
    toBooleanDiscItemKey,
    toNaturalDigitItemKey,
    toPrimitivePowerCollectionKey,
    toPrimitivePowerConstellationKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import { isPrimitiveDiscWheel, toPrimitiveHubRingRadius } from '@/adapters/primitive/_pure/derivation/wheels';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

/** Euler XYZ spins the local XZ ring about Y before tipping its plane about radial X. */
export function toPrimitiveBandRotation(spin: number, tilt = Math.PI / 2): [number, number, number] {
    return [tilt, spin, 0];
}

export function toPrimitiveBandSpin(wheel: PrimitiveWheelState, power: number): number {
    return wheel.rotationByPower?.[power] ?? 0;
}

export function toPrimitiveBandPosition(wheel: PrimitiveWheelState, power: number): [number, number, number] {
    return wheel.positionByPower?.[power] ?? wheel.position;
}

/** Matches the per-item angle a sector-ring item resolves to at its index in the rendered digit order. */
export function toNaturalDigitAngle(config: Config, digit: number): number {
    const renderedDigits = toNaturalRenderedDigits(config);
    const index = renderedDigits.indexOf(digit);
    return (index / renderedDigits.length) * Math.PI * 2;
}

export function buildNaturalPowerConstellationDerivations(
    config: Config,
    wheel: PrimitiveWheelState,
): EntityConstellationDerivation[] {
    return wheel.powers.map((power) => {
        // Radius follows the power, not the index, so a single-band flight wheel keeps its ring.
        const fullInnerRadius = config.base.display.wheel.innerRadius + power * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap);
        const growth = wheel.entranceGrowth;
        const hubRingRadius = toPrimitiveHubRingRadius(config);
        const grow = (radius: number) => growth === undefined ? radius : hubRingRadius + (radius - hubRingRadius) * growth;
        const innerRadius = grow(fullInnerRadius);
        const outerRadius = grow(fullInnerRadius + config.base.display.wheel.ringWidth);
        const bandRef = { wheelRole: wheel.role, variant: wheel.variant, power };

        return defineEntityConstellationDerivation({
            constellation: {
                constellationKind: 'entity',
                constellationKey: toPrimitivePowerConstellationKey(bandRef),
                itemCollectionKey: toPrimitivePowerCollectionKey(bandRef),
                anchor: {
                    itemKey: toNaturalDigitItemKey({ ...bandRef, digit: config.natural.digits[0] ?? 1 }),
                    anchorKey: 'center',
                },
                meta: {
                    wheelRole: wheel.role,
                    power,
                },
            },
            parametricState: {
                layout: {
                    layoutKind: 'sector-ring',
                    anchor: { anchorKind: 'position', position: toPrimitiveBandPosition(wheel, power) },
                    innerRadius,
                    outerRadius,
                    angularGapFraction: config.natural.display.wheel.angularGapFraction,
                    arcSegments: config.base.display.wheel.arcSegments,
                    ...(config.natural.display.cell.relief.enabled && outerRadius > innerRadius
                        ? { outerThickness: config.natural.display.cell.relief.thickness, thicknessProfile: 'uniform' as const }
                        : {}),
                    rotation: wheel.wheelRotation ?? toPrimitiveBandRotation(toPrimitiveBandSpin(wheel, power), wheel.bandTilt),
                },
                labelLayoutPolicy: {
                    placement: 'centered',
                    sizePolicy: 'fit-available-space',
                    minFontSize: 0.3,
                    maxFontSize: 0.68,
                    yOffset: 0.02,
                },
            },
        });
    });
}

export function buildBooleanDiscConstellationDerivations(
    config: Config,
    wheel: PrimitiveWheelState,
): EntityConstellationDerivation[] {
    if (!isPrimitiveDiscWheel(wheel)) {
        throw new Error('Natural wheels do not have disc constellations.');
    }
    const power = 0;
    const hubRadius = config.base.display.wheel.innerRadius;
    const hubRingRadius = toPrimitiveHubRingRadius(config);
    const discGrowth = wheel.valueKind === 'boolean' ? wheel.discGrowth ?? 1 : 0;
    return [defineEntityConstellationDerivation({
        constellation: {
            constellationKind: 'entity',
            constellationKey: toPrimitivePowerConstellationKey({ wheelRole: wheel.role, variant: wheel.variant, power }),
            itemCollectionKey: toPrimitivePowerCollectionKey({ wheelRole: wheel.role, variant: wheel.variant, power }),
            anchor: {
                itemKey: toBooleanDiscItemKey({ wheelRole: wheel.role, variant: wheel.variant, power }),
                anchorKey: 'center',
            },
            meta: { wheelRole: wheel.role, power },
        },
        parametricState: {
            layout: {
                layoutKind: 'sector-ring',
                anchor: { anchorKind: 'position', position: toPrimitiveBandPosition(wheel, power) },
                innerRadius: wheel.valueKind === 'boolean' ? hubRadius : hubRingRadius,
                outerRadius: wheel.valueKind === 'boolean'
                    ? hubRadius + config.base.display.wheel.ringWidth * discGrowth
                    : hubRingRadius,
                angularGapFraction: 0,
                arcSegments: config.base.display.wheel.arcSegments,
                rotation: wheel.wheelRotation ?? toPrimitiveBandRotation(toPrimitiveBandSpin(wheel, power)),
            },
        },
    })];
}